/**
 * CMS SDK - useMedia Hook
 * 媒体文件管理 Hook
 */

import { useState, useCallback } from 'react';
import { useCMS } from '../context/CMSContext';

/**
 * 媒体文件管理 Hook
 * @returns {Object} 媒体操作对象
 */
export function useMedia() {
  const { api } = useCMS();
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 24,
    total: 0,
    totalPages: 0,
  });

  /**
   * 加载媒体列表
   */
  const load = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getMediaList({
        page: pagination.page,
        page_size: pagination.pageSize,
        ...params,
      });
      const items = result.items || result.data || [];
      setMediaList(items);
      setPagination(prev => ({
        ...prev,
        total: result.total || items.length,
        totalPages: result.total_pages || Math.ceil((result.total || items.length) / prev.pageSize),
      }));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, pagination.page, pagination.pageSize]);

  /**
   * 上传媒体文件
   */
  const upload = useCallback(async (file) => {
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
      const result = await api.uploadMedia(file, (progress) => {
        setUploadProgress(progress);
      });
      setMediaList(prev => [result, ...prev]);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [api]);

  /**
   * 批量上传
   */
  const uploadMultiple = useCallback(async (files) => {
    const results = [];
    for (const file of files) {
      const result = await upload(file);
      results.push(result);
    }
    return results;
  }, [upload]);

  /**
   * 删除媒体文件
   */
  const remove = useCallback(async (mediaId) => {
    setLoading(true);
    setError(null);
    try {
      await api.deleteMedia(mediaId);
      setMediaList(prev => prev.filter(m => m.id !== mediaId));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 按类型筛选
   */
  const filterByType = useCallback(async (type) => {
    return load({ type });
  }, [load]);

  /**
   * 搜索媒体
   */
  const search = useCallback(async (keyword) => {
    return load({ keyword });
  }, [load]);

  /**
   * 设置页码
   */
  const setPage = useCallback((page) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  return {
    mediaList,
    loading,
    uploading,
    uploadProgress,
    error,
    pagination,
    load,
    upload,
    uploadMultiple,
    remove,
    filterByType,
    search,
    setPage,
  };
}

export default useMedia;
